(() => {
  const HOST_ID = "sfFitFeelHost";
  const DEFAULT_VIEW_NAME = "sf-fit-feel";
  const OPEN_ATTR = "data-sf-open";
  const CLOSING_ATTR = "data-sf-closing";

  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  let lastTrigger = null;
  let activeRoot = null;
  let activeDialog = null;
  let closeTimer = 0;

  const htmlCache = new Map();

  function getHost() {
    return qs(`#${HOST_ID}`);
  }

  function getViewName() {
    const host = getHost();
    return (host && host.getAttribute("data-sf-ff-view")) || DEFAULT_VIEW_NAME;
  }

  function getFocusable(dialog) {
    if (!dialog) return [];

    const els = qsa(
      [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])'
      ].join(","),
      dialog
    );

    return els.filter((el) => {
      const style = window.getComputedStyle(el);
      return style.display !== "none" && style.visibility !== "hidden";
    });
  }

  function onDialogKeydown(e) {
    if (!activeDialog) return;

    if (e.key === "Escape") {
      e.preventDefault();
      closeDrawer(activeRoot);
      return;
    }

    if (e.key !== "Tab") return;

    const focusables = getFocusable(activeDialog);
    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first || document.activeElement === activeDialog) {
        e.preventDefault();
        last.focus({ preventScroll: true });
      }
    } else if (document.activeElement === last) {
      e.preventDefault();
      first.focus({ preventScroll: true });
    }
  }

  function cleanupActiveDialog() {
    if (activeDialog) {
      activeDialog.removeEventListener("keydown", onDialogKeydown);
    }
    activeRoot = null;
    activeDialog = null;
  }

  function closeDrawer(root) {
    if (!root) return;

    root.removeAttribute(OPEN_ATTR);
    root.setAttribute(CLOSING_ATTR, "true");
    root.setAttribute("aria-hidden", "true");
    document.documentElement.style.overflow = "";

    cleanupActiveDialog();

    const drawer = qs(".sf-ff__drawer", root);

    function done() {
      root.hidden = true;
      root.removeAttribute(CLOSING_ATTR);

      if (lastTrigger && typeof lastTrigger.focus === "function") {
        try {
          lastTrigger.focus({ preventScroll: true });
        } catch (e) {}
      }
    }

    clearTimeout(closeTimer);

    if (!drawer) {
      done();
      return;
    }

    const onEnd = (e) => {
      if (e.target !== drawer) return;
      drawer.removeEventListener("transitionend", onEnd);
      done();
    };

    drawer.addEventListener("transitionend", onEnd);

    closeTimer = window.setTimeout(() => {
      drawer.removeEventListener("transitionend", onEnd);
      if (!root.hidden) done();
    }, 260);
  }

  function openDrawer(root) {
    if (!root) return;

    clearTimeout(closeTimer);

    root.hidden = false;
    root.setAttribute("aria-hidden", "false");
    root.removeAttribute(CLOSING_ATTR);

    document.documentElement.style.overflow = "hidden";

    const dialog = qs('[role="dialog"]', root);
    activeRoot = root;
    activeDialog = dialog || null;

    requestAnimationFrame(() => {
      root.setAttribute(OPEN_ATTR, "true");

      if (activeDialog) {
        activeDialog.addEventListener("keydown", onDialogKeydown);
        try {
          activeDialog.focus({ preventScroll: true });
        } catch (e) {}
      }

      if (typeof root.__sfFfSyncBar === "function") {
        root.__sfFfSyncBar();
      }
    });
  }

  function initMoreProductsScrollbar(root) {
    const row = qs(".sf-ff__moreRow", root);
    const thumb = qs(".sf-ff__moreScrollThumb", root);
    const track = qs(".sf-ff__moreScroll", root);
    if (!row || !thumb || !track) return;
    if (row.__sfFfBarInit) return;
    row.__sfFfBarInit = true;

    let raf = 0;
    let resizeRaf = 0;

    function update() {
      raf = 0;

      const maxScroll = row.scrollWidth - row.clientWidth;

      if (maxScroll <= 0) {
        track.style.display = "none";
        thumb.style.width = "0px";
        thumb.style.transform = "translateX(0px)";
        return;
      }

      track.style.display = "";

      const thumbPct = (row.clientWidth / row.scrollWidth) * 100;
      const clampedThumbPct = Math.max(10, Math.min(100, thumbPct));
      thumb.style.width = clampedThumbPct + "%";

      const endEpsilon = 2;
      const isAtEnd = (row.scrollLeft + row.clientWidth) >= (row.scrollWidth - endEpsilon);

      let progress = 0;
      if (isAtEnd) {
        progress = 1;
      } else if (maxScroll > 0) {
        progress = row.scrollLeft / maxScroll;
      }

      progress = Math.max(0, Math.min(1, progress));

      const trackW = track.clientWidth;
      const thumbW = thumb.getBoundingClientRect().width;
      const travelPx = Math.max(0, trackW - thumbW);
      const xPx = progress * travelPx;

      thumb.style.transform = `translateX(${xPx}px)`;
    }

    function requestUpdate() {
      if (raf) return;
      raf = requestAnimationFrame(update);
    }

    function onResize() {
      if (resizeRaf) return;
      resizeRaf = requestAnimationFrame(() => {
        resizeRaf = 0;
        requestUpdate();
      });
    }

    row.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", onResize, { passive: true });

    if (window.ResizeObserver) {
      const ro = new ResizeObserver(requestUpdate);
      ro.observe(row);
      ro.observe(track);
      root.__sfFfResizeObserver = ro;
    }

    root.__sfFfSyncBar = requestUpdate;

    requestUpdate();

    row.querySelectorAll("img").forEach((img) => {
      if (img.complete) return;
      img.addEventListener("load", requestUpdate, { once: true });
    });
  }

  function bindInteractions(root) {
    if (root.__sfFfBound) return;
    root.__sfFfBound = true;

    root.addEventListener("click", (e) => {
      const closeBtn = e.target.closest("[data-sf-ff-close]");
      if (closeBtn) {
        closeDrawer(root);
      }
    });
  }

  async function loadFitFeel(handle) {
    const host = getHost();
    if (!host || !handle) return;

    const view = getViewName();
    const cacheKey = `${handle}::${view}`;

    let html = htmlCache.get(cacheKey);

    if (!html) {
      const url = `/products/${encodeURIComponent(handle)}?view=${encodeURIComponent(view)}`;
      const res = await fetch(url, { credentials: "same-origin" });
      if (!res.ok) return;
      html = await res.text();
      htmlCache.set(cacheKey, html);
    }

    host.innerHTML = html;

    const root = qs("[data-sf-fit-feel-root]", host);
    if (!root) return;

    bindInteractions(root);
    initMoreProductsScrollbar(root);
    openDrawer(root);
  }

  document.addEventListener(
    "click",
    (e) => {
      const btn = e.target.closest("[data-sf-fit-feel]");
      if (!btn) return;

      const handle = btn.getAttribute("data-product-handle");
      if (!handle) return;

      lastTrigger = btn;
      e.preventDefault();
      loadFitFeel(handle);
    },
    true
  );

  window.SFFitFeel = window.SFFitFeel || {};
  window.SFFitFeel.open = (handle) => loadFitFeel(handle);
  window.SFFitFeel.close = () => {
    const host = getHost();
    const root = host ? qs("[data-sf-fit-feel-root]", host) : null;
    closeDrawer(root);
  };
})();